import type { AnalyticsProvider } from './index';

export class SegmentProvider implements AnalyticsProvider {
  identify(userId?: string, traits?: Record<string, unknown>, options?: Record<string, unknown>): void {
    if (typeof window !== 'undefined' && window.analytics) {
      window.analytics.identify(userId, traits, { ...options });
    }
  }

  page(name?: string, properties?: Record<string, unknown>): void {
    if (typeof window !== 'undefined' && window.analytics) {
      window.analytics.page(name, properties);
    }
  }

  track(
    event: string,
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
    callback?: () => void,
  ): void {
    if (typeof window !== 'undefined' && window.analytics) {
      if (callback) {
        window.analytics.track(event, properties, { ...options }, callback);
        return;
      }

      window.analytics.track(event, properties, { ...options });
    }
  }

  anonymousId() {
    if (typeof window !== 'undefined' && window.analytics && window.analytics?.user) {
      return window.analytics.user().anonymousId();
    }

    return null;
  }

  /**
   * Drops the stored `ajs_user_id` and rolls a fresh `ajs_anonymous_id`.
   *
   * Both halves matter on sign-out. Clearing the user id alone would leave the
   * next visitor on this browser sharing the previous account's anonymous id,
   * which Mixpanel has already merged into that account — so their pre-identify
   * events would still land on someone else's profile.
   */
  reset(): void {
    identityMethods()?.reset?.();
  }

  /**
   * Adopts an anonymous id minted on another origin. See
   * `lib/analytics/segment-anonymous-id.ts`.
   *
   * The snippet does this too, and earlier — this is for callers that learn the
   * id after Segment has loaded. Setting the same value twice is a no-op.
   */
  setAnonymousId(anonymousId: string): void {
    identityMethods()?.setAnonymousId?.(anonymousId);
  }

  /**
   * Whoever Segment currently thinks this browser is, which is not always anyone we identified.
   *
   * Null while the snippet's stub is still queuing — `user` is not in its `methods` list — which is
   * a different thing from nobody. `readStoredSegmentUserId` covers that window.
   */
  userId(): string | null {
    return identityMethods()?.user?.().id() ?? null;
  }
}

/**
 * The identity surface, read through a local cast.
 *
 * `reset` and `setAnonymousId` are in the snippet's `methods` list, so a call made before
 * analytics.js arrives is queued rather than lost. `user` is NOT — it only exists once the library
 * has loaded, which is why every read of it has to treat absence as "cannot tell" rather than as
 * "nobody".
 *
 * Cast rather than declared: the ambient type this app has for `window.analytics` describes none of
 * them, and widening a global from here has a far larger blast radius than these call sites.
 */
interface SegmentIdentityMethods {
  reset?: () => void;
  setAnonymousId?: (anonymousId: string) => void;
  user?: () => { id: () => string | null };
}

function identityMethods(): SegmentIdentityMethods | undefined {
  if (typeof window === 'undefined') {
    return;
  }

  return (window as unknown as { analytics?: SegmentIdentityMethods }).analytics;
}
